
import { Link } from 'react-router-dom';
import { ChevronRight, Calendar, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { TrainingSession } from '@/shared/types/coach.types';

interface SessionListProps {
  sessions: TrainingSession[];
  isLoading?: boolean;
  onSessionClick?: (session: TrainingSession) => void;
  showCalendarLink?: boolean;
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function SessionList({ sessions, isLoading, onSessionClick, showCalendarLink = true }: SessionListProps) {
  const calendarIcon = showCalendarLink ? (
    <Link to="/coach/sessions?view=calendar" className="hover:text-primary transition-colors">
      <Calendar className="w-5 h-5 text-muted-foreground hover:text-primary" />
    </Link>
  ) : (
    <Calendar className="w-5 h-5 text-muted-foreground" />
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Today&apos;s Schedule</CardTitle>
          {calendarIcon}
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Today&apos;s Schedule</CardTitle>
        {calendarIcon}
      </CardHeader>
      <CardContent className="space-y-3">
        {sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No sessions scheduled for today
          </p>
        ) : (
          sessions.map(session => (
            <div
              key={session.id}
              onClick={() => onSessionClick?.(session)}
              className="flex items-center justify-between p-3 bg-secondary rounded-lg hover:bg-secondary/70 transition cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold text-primary w-20">
                  {formatTime(session.startTime)}
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    {session.sessionType
                      ? session.sessionType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) + ' Session'
                      : session.name}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <span>{session.courtLocation || 'TBD'} &bull; {session.playerCount ?? 0} players</span>
                    {session.hasNote && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-primary">
                        <FileText className="w-3 h-3" />
                        Note
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
